import React, { useEffect, useState } from "react";
import Header from "../Header";
import { supabase } from "../../../supabase";
import { Link } from "react-router-dom";
import GoToTop from "../../../GoToTop";
import Cropper from "react-easy-crop";
import { getCroppedWebp } from "./imageUtils";

function GenSecratary() {
  const [secretary, setSecretary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // modal states
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({
    id: "",
    name: "",
    from_date: "",
    to_date: "",
    image_url: "",
  });

  // image crop states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);

  useEffect(() => {
    fetchSecretaries();
  }, []);

  const fetchSecretaries = async () => {
    try {
      const { data, error } = await supabase
        .from("general_secretary")
        .select("id, name, from_date, to_date, image_url")
        .order("from_date", { ascending: true });

      if (error) throw error;
      setSecretary(data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // open edit modal
  const openEditModal = (item) => {
    setEditData(item);
    setImageSrc(item.image_url || null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedPixels(null);
    setImageChanged(false);
    setShowModal(true);
  };

  const onCropComplete = (_, croppedAreaPixels) => {
    setCroppedPixels(croppedAreaPixels);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setImageChanged(true);
    };
    reader.readAsDataURL(file);
  };

  // update record + image
  const handleUpdate = async () => {
    if (updating) return;

    try {
      setUpdating(true);

      // 1️⃣ update text fields
      const { error } = await supabase
        .from("general_secretary")
        .update({
          name: editData.name,
          from_date: editData.from_date,
          to_date: editData.to_date,
        })
        .eq("id", editData.id);

      if (error) throw error;

      // 2️⃣ update image ONLY if changed
      if (imageChanged && croppedPixels) {
        const webpBlob = await getCroppedWebp(imageSrc, croppedPixels);
        const filePath = `${editData.id}.webp`;

        const { error: uploadError } = await supabase.storage
          .from("general_secretary")
          .upload(filePath, webpBlob, {
            contentType: "image/webp",
            upsert: true,
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("general_secretary")
          .getPublicUrl(filePath);

        const imageUrl = `${urlData.publicUrl}?t=${Date.now()}`;

        await supabase
          .from("general_secretary")
          .update({ image_url: imageUrl })
          .eq("id", editData.id);
      }

      setShowModal(false);
      fetchSecretaries();
    } catch (err) {
      console.error(err);
      alert("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <GoToTop />
      <main className="main-content" id="mainContent">
        <Header heading="General Secretary" />

        <Link className="btn btn-dark mb-2" to="/admin-add-gen-secretary">
          Add General Secretary
        </Link>

        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>SNo.</th>
              <th>Profile</th>
              <th>Name</th>
              <th>From Date</th>
              <th>To Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : secretary.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-danger">
                  No Records Found
                </td>
              </tr>
            ) : (
              secretary.map((item, index) => (
                <tr key={item.id}>
                  <td style={{ width: 50, textAlign: "center" }}>
                    {index + 1}
                  </td>
                  <td style={{ width: 70, padding: 1 }}>
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt=""
                        style={{ width: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      ""
                    )}
                  </td>
                  <td>{item.name}</td>
                  <td>{item.from_date}</td>
                  <td>{item.to_date}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openEditModal(item)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </main>

      {/* ===== EDIT MODAL ===== */}
      {showModal && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit General Secretary</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-5">
                    {imageSrc && (
                      <div
                        style={{
                          position: "relative",
                          height: 300,
                          marginBottom: 10,
                        }}
                      >
                        <Cropper
                          image={imageSrc}
                          crop={crop}
                          zoom={zoom}
                          aspect={3 / 4}
                          onCropChange={setCrop}
                          onZoomChange={setZoom}
                          onCropComplete={onCropComplete}
                        />
                      </div>
                    )}

                    <label>Profile Image</label>
                    <input
                      type="file"
                      className="form-control mb-3"
                      accept="image/*"
                      onChange={handleImageSelect}
                    />
                  </div>

                  <div className="col-lg-7">
                    <div className="mb-2">
                      <label>Name</label>
                      <input
                        className="form-control"
                        value={editData.name}
                        onChange={(e) =>
                          setEditData({ ...editData, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="mb-2">
                      <label>From Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={editData.from_date}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            from_date: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="mb-2">
                      <label>To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={editData.to_date}
                        onChange={(e) =>
                          setEditData({ ...editData, to_date: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleUpdate}
                  disabled={updating}
                >
                  {updating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GenSecratary;
